import { useMemo, useState } from "react";
import { AvatarStack, type AvatarUser } from "@/components/ui/Avatar";
import { Button, IconButton } from "@/components/ui/Button";
import { GoalDateControl } from "@/components/board/GoalDateControl";
import { BoardDescription } from "@/components/board/BoardDescription";
import { RenameBoardDialog } from "@/components/dialogs/RenameBoardDialog";
import { TicketSettingsDialog } from "@/components/dialogs/TicketSettingsDialog";
import { IdeenboxSettingsDialog } from "@/components/dialogs/IdeenboxSettingsDialog";
import { AddRowDialog } from "@/components/board/AddRowDialog";
import { BRAND } from "@/lib/tokens";
import { deriveAvatarColor, deriveInitials } from "@/lib/colors";
import { useAuth } from "@/hooks/useAuth";
import { useBoardPeople } from "@/hooks/useBoardPeople";
import { useBoardPresence } from "@/hooks/useBoardPresence";
import { usePersonalWorkspaceId } from "@/hooks/useWorkspaceMeta";
import { useWorkspaceMembers } from "@/hooks/useWorkspaceMembers";
import { usePinnedBoards, isBoardPinned } from "@/hooks/usePinnedBoards";
import { useUpdateBoard } from "@/hooks/useUpdateBoard";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { ViewBoard } from "@/lib/boardView";
import type { Board, ColumnDef, UUID } from "@/lib/types";

interface BoardSubheaderProps {
  board: ViewBoard;
  // Real backend ids — required for pin + rename (the fixture path passes
  // null, in which case star/edit fall back to toast).
  boardId: UUID | null;
  workspaceId: UUID | null;
  onAddRow?: () => void;
  /** Grouping column id — its field in the add-row modal gets "+ Neue Gruppe". */
  groupColumnId?: string | null;
  onAddColumnOption?: (
    column: ColumnDef,
    label: string,
  ) => Promise<{ id: string; label: string; color: string } | null>;
}

export function BoardSubheader({
  board,
  boardId,
  workspaceId,
  onAddRow,
  groupColumnId = null,
  onAddColumnOption,
}: BoardSubheaderProps) {
  const presentIds = useBoardPresence(boardId ?? undefined);
  const people = useBoardPeople(boardId ?? undefined);
  const members = useWorkspaceMembers(workspaceId ?? undefined);
  const liveUsers = useMemo<AvatarUser[]>(() => {
    // Resolve viewer names from BOTH the workspace members and the board's
    // people directory — a viewer who isn't assigned to any row is still a
    // member, and was previously dropped (so presence looked broken). Workspace
    // members seed the map; board people (e.g. cross-workspace guests) layer on.
    const byId = new Map<string, AvatarUser>();
    const put = (
      user_id: string,
      name: string,
      username: string | null,
      deptWsId: string | null,
      deptColor: string | null,
    ) =>
      byId.set(user_id, {
        id: user_id,
        display_name: name,
        initials: deriveInitials(name),
        avatar_color: deriveAvatarColor(user_id),
        username,
        department_workspace_id: deptWsId,
        department_color: deptColor,
      });
    for (const m of members.data ?? [])
      put(
        m.user_id,
        m.display_name ?? m.email,
        m.username,
        m.department_workspace_id ?? null,
        m.department_color ?? null,
      );
    for (const p of people.data ?? [])
      put(
        p.user_id,
        p.display_name ?? p.email,
        p.username,
        p.department_workspace_id ?? null,
        p.department_color ?? null,
      );
    // Keep viewers we still can't name (e.g. a shared-in member of another
    // workspace) so the count stays honest — show a neutral placeholder avatar.
    return presentIds.map(
      (id) =>
        byId.get(id) ?? {
          id,
          display_name: "?",
          initials: "?",
          avatar_color: deriveAvatarColor(id),
          username: null,
        },
    );
  }, [presentIds, people.data, members.data]);

  // Identify "me" among the viewers (the FE doesn't carry its own internal
  // users.id, so match by email/username against the directory) and count only
  // the OTHERS — the stack shows everyone's icon, but "N sieht/sehen mit" means
  // other people here, and the indicator hides when you're alone.
  const { user } = useAuth();
  const myId = useMemo(() => {
    const email = user?.email?.toLowerCase();
    const username = user?.username?.toLowerCase();
    const find = (arr: { user_id: string; email: string; username: string | null }[]) =>
      arr.find(
        (m) =>
          (email && m.email.toLowerCase() === email) ||
          (username && m.username?.toLowerCase() === username),
      )?.user_id;
    return find(members.data ?? []) ?? find(people.data ?? []) ?? null;
  }, [user?.email, user?.username, members.data, people.data]);
  const othersCount = useMemo(
    () => liveUsers.filter((u) => u.id !== myId).length,
    [liveUsers, myId],
  );
  // Personal boards are creator-only — they can never be shared, so nobody else
  // can ever legitimately view them. Suppress the "X sieht mit" indicator there:
  // on a personal board the viewer's own session can't be matched to self (the
  // system "Persönlich" workspace has no normal membership / board people), so it
  // mis-counts the owner as "1 sieht mit" and shows a nameless "?" avatar — which
  // reads as an admin/bot snooping a private board.
  const personalWorkspaceId = usePersonalWorkspaceId();
  const isPersonalBoard = !!workspaceId && workspaceId === personalWorkspaceId;

  const pinned = usePinnedBoards();
  const isPinned = boardId ? isBoardPinned(pinned.data, boardId) : false;
  const [renameOpen, setRenameOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [ideenSettingsOpen, setIdeenSettingsOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const addColumns = useMemo<ColumnDef[]>(
    () => board.columns.map((c) => c.raw).filter((c): c is ColumnDef => Boolean(c)),
    [board.columns],
  );
  const updateBoard = useUpdateBoard();
  const showTicketSettings = !!boardId && board.kind === "tickets" && board.access !== "guest";
  // Ideenbox admin panel (default assignee). Admin-only — it configures triage
  // routing, unlike the view-preference settings any member may touch.
  const showIdeenSettings = !!boardId && board.kind === "ideas" && board.canAdmin;

  const togglePin = () => {
    if (!boardId) return showToast(t.comingSoon);
    if (isPinned) {
      pinned.unpin.mutate({ boardId });
    } else {
      // Manufacture the minimum Board shape needed for optimistic insertion.
      pinned.pin.mutate({
        board: {
          id: boardId,
          workspace_id: workspaceId ?? boardId,
          name: board.name,
          description: board.description ?? null,
          color: null,
          created_by: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as Board,
      });
    }
  };

  return (
    <div
      style={{
        padding: "16px 18px 12px",
        borderBottom: "1px solid var(--hairline-soft)",
        display: "flex",
        alignItems: "flex-start",
        gap: 16,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 19,
              fontWeight: 600,
              color: "var(--ink-1)",
              letterSpacing: "-0.01em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {board.name}
          </h1>
          {boardId && workspaceId ? (
            <IconButton
              icon="edit"
              size={26}
              title={t.renameBoard}
              onClick={() => setRenameOpen(true)}
            />
          ) : null}
          <IconButton
            icon="star"
            size={26}
            active={isPinned}
            color={isPinned ? BRAND.orange : undefined}
            title={isPinned ? t.unpinBoard : t.pinBoard}
            onClick={togglePin}
          />
        </div>
        <BoardDescription storageId={boardId ?? board.name} description={board.description} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {othersCount > 0 && !isPersonalBoard ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px 4px 6px",
              background: "var(--surface-soft)",
              borderRadius: 999,
              border: "1px solid var(--hairline)",
            }}
          >
            <AvatarStack users={liveUsers} size={22} max={3} />
            <span style={{ fontSize: 11.5, color: "var(--ink-2)", fontWeight: 500 }}>
              <span
                style={{
                  display: "inline-block",
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: "#5E8B5F",
                  marginRight: 5,
                  boxShadow: "0 0 0 2px rgba(94,139,95,0.18)",
                }}
              />
              {othersCount} {othersCount === 1 ? t.viewingNowOne : t.viewingNow}
            </span>
          </div>
        ) : null}
        {boardId && workspaceId && board.kind !== "tickets" ? (
          <GoalDateControl
            value={board.goalDate}
            onChange={(d) =>
              updateBoard.mutate({ boardId, workspaceId, payload: { goal_date: d } })
            }
          />
        ) : null}
        {showTicketSettings ? (
          <IconButton
            icon="sliders"
            size={30}
            title={t.ticketSettingsMenuItem}
            onClick={() => setSettingsOpen(true)}
          />
        ) : null}
        {showIdeenSettings ? (
          <IconButton
            icon="sliders"
            size={30}
            title={t.ideenSettingsMenuItem}
            onClick={() => setIdeenSettingsOpen(true)}
          />
        ) : null}
        <Button
          variant="secondary"
          size="sm"
          icon="upload"
          title={t.comingSoon}
          onClick={() => showToast(t.comingSoon)}
        >
          {t.share}
        </Button>
        {onAddRow ? (
          <Button
            variant="accent"
            size="sm"
            icon="plus"
            title={t.addRow}
            onClick={() => (boardId ? setAddOpen(true) : onAddRow())}
          >
            {t.addRow}
          </Button>
        ) : null}
      </div>

      {boardId ? (
        <AddRowDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          boardId={boardId}
          columns={addColumns}
          groupColumnId={groupColumnId}
          onAddColumnOption={onAddColumnOption}
        />
      ) : null}

      {boardId && workspaceId ? (
        <RenameBoardDialog
          open={renameOpen}
          onOpenChange={setRenameOpen}
          boardId={boardId}
          workspaceId={workspaceId}
          initialName={board.name}
          initialDescription={board.description}
        />
      ) : null}

      {showTicketSettings && boardId ? (
        <TicketSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          board={board}
          boardId={boardId}
        />
      ) : null}

      {showIdeenSettings && boardId && workspaceId ? (
        <IdeenboxSettingsDialog
          open={ideenSettingsOpen}
          onOpenChange={setIdeenSettingsOpen}
          board={board}
          boardId={boardId}
          workspaceId={workspaceId}
        />
      ) : null}
    </div>
  );
}
